/**
 * Persistence Drift tab — snapshot-vs-baseline diff across the fleet.
 * Spec: LOVABLE_PERSISTENCE_PAGE.md
 */
import { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { AlertTriangle, Info, MoreHorizontal, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AttestationDiff } from "@/components/lighthouse/AttestationDiff";

import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { OwnerPill } from "@/components/owner-pill";

import {
  useAddApprovedWriter,
  useApprovedWriters,
  usePersistenceBaselines,
  usePersistenceSnapshots,
  useRemoveApprovedWriter,
  useReplaceBaseline,
} from "@/hooks/security/usePersistence";
import type {
  ApprovedWriter,
  PersistenceCheckType,
  PersistenceItem,
  PersistenceItemStatus,
  PersistenceSnapshot,
  WriterKind,
} from "@/types/edrHunts";

const CHECK_TYPES: PersistenceCheckType[] = [
  "pam",
  "sudoers",
  "ssh_keys",
  "local_accounts",
  "btm",
  "authdb",
  "launchd",
  "cron",
];

const CHECK_COLOR: Record<string, string> = {
  ssh_keys: "bg-purple-500/10 text-purple-500 border-purple-500/30",
  sudoers: "bg-destructive/10 text-destructive border-destructive/30",
  launchd: "bg-info/10 text-info border-info/30",
  pam: "bg-warning/10 text-warning border-warning/30",
  btm: "bg-info/10 text-info border-info/30",
  authdb: "bg-warning/10 text-warning border-warning/30",
  local_accounts: "bg-accent text-accent-foreground border-border",
  cron: "bg-accent text-accent-foreground border-border",
};

const STATUS_COLOR: Record<PersistenceItemStatus, string> = {
  match: "bg-success/10 text-success border-success/30",
  drift: "bg-destructive/10 text-destructive border-destructive/30",
  new: "bg-warning/10 text-warning border-warning/30",
  missing: "bg-muted text-muted-foreground border-border",
};

interface PersistenceTabProps {
  filters: {
    deviceId?: string;
    checkType?: string;
    status?: string; // "drift,new,missing"
  };
  onFiltersChange: (next: { deviceId?: string; checkType?: string; status?: string }) => void;
}

interface FlatRow {
  snapshot: PersistenceSnapshot;
  item: PersistenceItem;
  key: string;
}

type MatchOn = "path" | "name" | "name+user" | null;

/**
 * Find the baseline entry that corresponds to a snapshot item.
 * Matches on path first (case-insensitive), then on (name + user), then on name.
 */
function matchBaselineEntry(
  entries: PersistenceItem[] | undefined,
  item: PersistenceItem,
): { entry: PersistenceItem | null; on: MatchOn } {
  if (!entries || entries.length === 0) return { entry: null, on: null };
  if (item.path) {
    const p = item.path.toLowerCase();
    const hit = entries.find((e) => e.path && e.path.toLowerCase() === p);
    if (hit) return { entry: hit, on: "path" };
  }
  if (item.name && item.user) {
    const hit = entries.find((e) => e.name === item.name && e.user === item.user);
    if (hit) return { entry: hit, on: "name+user" };
  }
  if (item.name) {
    const hit = entries.find((e) => e.name === item.name);
    if (hit) return { entry: hit, on: "name" };
  }
  return { entry: null, on: null };
}

export function PersistenceTab({ filters, onFiltersChange }: PersistenceTabProps) {
  const snapshotsQ = usePersistenceSnapshots({
    deviceId: filters.deviceId,
    checkType: filters.checkType,
  });
  const snapshots = snapshotsQ.data ?? [];
  const baselinesQ = usePersistenceBaselines();
  const baselines = baselinesQ.data ?? [];

  // Lookup baseline entries by check type (for diff rendering).
  const baselineByCheck = useMemo(() => {
    const m = new Map<string, PersistenceItem[]>();
    for (const b of baselines) {
      if (b.checkType) m.set(String(b.checkType), b.entries ?? []);
    }
    return m;
  }, [baselines]);

  // Which check types are missing a baseline among the currently visible snapshots?
  const checksMissingBaseline = useMemo(() => {
    const seen = new Set<string>();
    for (const s of snapshots) {
      const ct = String(s.checkType ?? "");
      if (!ct) continue;
      if (!baselineByCheck.has(ct)) seen.add(ct);
    }
    return Array.from(seen);
  }, [snapshots, baselineByCheck]);

  const [writersOpen, setWritersOpen] = useState(false);
  const [baselinesOpen, setBaselinesOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<FlatRow | null>(null);
  const [approveTarget, setApproveTarget] = useState<FlatRow | null>(null);

  const statusFilter = (filters.status?.split(",").filter(Boolean) as PersistenceItemStatus[]) ?? [
    "drift",
    "new",
    "missing",
  ];

  // Flatten snapshots[].items[] for the table.
  const rows: FlatRow[] = useMemo(() => {
    const out: FlatRow[] = [];
    for (const s of snapshots) {
      const items = s.items ?? [];
      for (let i = 0; i < items.length; i++) {
        out.push({
          snapshot: s,
          item: items[i],
          key: `${s.id ?? s.deviceId}-${s.checkType}-${i}`,
        });
      }
    }
    return out.filter((r) => {
      const st = (r.item.status ?? "match") as PersistenceItemStatus;
      return statusFilter.includes(st);
    });
  }, [snapshots, statusFilter]);

  // Summary counts.
  const summary = useMemo(() => {
    let nNew = 0;
    let nDrift = 0;
    let nMissing = 0;
    const deviceIds = new Set<string>();
    for (const s of snapshots) {
      if (s.deviceId) deviceIds.add(s.deviceId);
      nNew += s.summary?.new ?? 0;
      nDrift += s.summary?.drifted ?? 0;
      nMissing += s.summary?.missing ?? 0;
    }
    return { nNew, nDrift, nMissing, devices: deviceIds.size };
  }, [snapshots]);

  // Devices for filter dropdown.
  const devices = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of snapshots) {
      if (s.deviceId) m.set(s.deviceId, s.hostname ?? s.deviceName ?? s.deviceId);
    }
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [snapshots]);

  const toggleStatus = (st: PersistenceItemStatus) => {
    const set = new Set(statusFilter);
    if (set.has(st)) set.delete(st);
    else set.add(st);
    const next = Array.from(set);
    onFiltersChange({ status: next.length ? next.join(",") : undefined });
  };

  return (
    <>
      <Panel
        title="Persistence drift"
        subtitle={
          <span className="text-xs text-muted-foreground">
            <button
              onClick={() => onFiltersChange({ status: "new" })}
              className="hover:text-foreground"
            >
              <b className="text-foreground">{summary.nNew}</b> new
            </button>
            {" · "}
            <button
              onClick={() => onFiltersChange({ status: "drift" })}
              className="hover:text-foreground"
            >
              <b className="text-foreground">{summary.nDrift}</b> changed
            </button>
            {" · "}
            <button
              onClick={() => onFiltersChange({ status: "missing" })}
              className="hover:text-foreground"
            >
              <b className="text-foreground">{summary.nMissing}</b> missing
            </button>{" "}
            across <b className="text-foreground">{summary.devices}</b> devices
          </span>
        }
        loading={snapshotsQ.isLoading}
        isError={snapshotsQ.isError}
        error={snapshotsQ.error}
        onRetry={() => snapshotsQ.refetch()}
        empty={!snapshotsQ.isLoading && !snapshotsQ.isError && snapshots.length === 0}
        emptyTitle="No persistence snapshots yet"
        emptyDescription="The sensor takes snapshots automatically every hour. Once a sensor has been online for at least an hour and posted its first snapshot, its persistence state appears here."
        skeleton="table"
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8" onClick={() => setWritersOpen(true)}>
              <Info className="h-3.5 w-3.5 mr-1.5" />
              Approved writers
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => setBaselinesOpen(true)}>
              Manage baselines
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button size="sm" variant="outline" className="h-8" disabled>
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    Run snapshot
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Snapshots run hourly per device.</TooltipContent>
            </Tooltip>
          </div>
        }
      >
        {/* Filters */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Select
            value={filters.deviceId ?? "all"}
            onValueChange={(v) =>
              onFiltersChange({ deviceId: v === "all" ? undefined : v })
            }
          >
            <SelectTrigger className="h-8 w-[200px] text-xs">
              <SelectValue placeholder="Device" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All devices</SelectItem>
              {devices.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.checkType ?? "all"}
            onValueChange={(v) =>
              onFiltersChange({ checkType: v === "all" ? undefined : v })
            }
          >
            <SelectTrigger className="h-8 w-[160px] text-xs">
              <SelectValue placeholder="Check type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All checks</SelectItem>
              {CHECK_TYPES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1">
            {(["drift", "new", "missing", "match"] as PersistenceItemStatus[]).map((st) => (
              <button
                key={st}
                onClick={() => toggleStatus(st)}
                className={cn(
                  "rounded border px-2 py-1 text-[10px] uppercase tracking-wider",
                  statusFilter.includes(st)
                    ? STATUS_COLOR[st]
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {st}
              </button>
            ))}
          </div>
        </div>

        {/* Baseline-not-set banner */}
        {checksMissingBaseline.length > 0 && (
          <Alert className="mb-3 border-warning/40 bg-warning/5 text-foreground">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <AlertTitle className="text-xs font-semibold">
              No baseline set for {checksMissingBaseline.length === 1 ? "1 check type" : `${checksMissingBaseline.length} check types`}
            </AlertTitle>
            <AlertDescription className="text-[11px] text-muted-foreground">
              Without a baseline, every entry is reported as "new" and drift comparison is unavailable.{" "}
              <button
                className="underline underline-offset-2 hover:text-foreground"
                onClick={() => setBaselinesOpen(true)}
              >
                Set baseline now
              </button>{" "}
              for: {checksMissingBaseline.join(", ")}.
            </AlertDescription>
          </Alert>
        )}

        {/* Drift table */}
        {rows.length === 0 ? (
          <div className="py-8 text-center text-xs text-muted-foreground">
            No drift items match the current filters.
          </div>
        ) : (
          <DriftTable
            rows={rows}
            onOpen={setSelectedRow}
            onApprove={setApproveTarget}
          />
        )}
      </Panel>

      {/* Detail drawer */}
      <Sheet open={!!selectedRow} onOpenChange={(o) => !o && setSelectedRow(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {selectedRow && (
            <ItemDrawer
              row={selectedRow}
              baselineEntries={baselineByCheck.get(String(selectedRow.snapshot.checkType ?? "")) ?? null}
              onApprove={() => {
                setApproveTarget(selectedRow);
                setSelectedRow(null);
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Approve into baseline dialog */}
      <ApproveIntoBaselineDialog
        target={approveTarget}
        onOpenChange={(o) => !o && setApproveTarget(null)}
        existingBaselineEntries={(checkType) => {
          // We'll fetch on demand inside the dialog via the hook.
          return checkType;
        }}
      />

      {/* Approved writers dialog */}
      <ApprovedWritersDialog open={writersOpen} onOpenChange={setWritersOpen} />

      {/* Baselines dialog */}
      <BaselinesDialog open={baselinesOpen} onOpenChange={setBaselinesOpen} />
    </>
  );
}

/* ───────────────────────── Drift table ───────────────────────── */

function DriftTable({
  rows,
  onOpen,
  onApprove,
}: {
  rows: FlatRow[];
  onOpen: (r: FlatRow) => void;
  onApprove: (r: FlatRow) => void;
}) {
  return (
    <div className="overflow-x-auto -mx-4 -my-4">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 text-left">Device</th>
            <th className="px-3 py-2 text-left w-[140px]">Owner</th>
            <th className="px-3 py-2 text-left w-[110px]">Check</th>
            <th className="px-3 py-2 text-left">Item</th>
            <th className="px-3 py-2 text-left w-[100px]">User</th>
            <th className="px-3 py-2 text-left w-[80px]">Status</th>
            <th className="px-3 py-2 text-left w-[100px]">Last sync</th>
            <th className="px-3 py-2 text-right w-[60px]" />
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 500).map((r) => {
            const st = (r.item.status ?? "match") as PersistenceItemStatus;
            const check = String(r.snapshot.checkType ?? "—");
            return (
              <tr
                key={r.key}
                className="border-b border-border/60 hover:bg-muted/40 cursor-pointer"
                onClick={() => onOpen(r)}
              >
                <td className="px-3 py-2 font-mono text-foreground truncate max-w-[160px]">
                  {r.snapshot.hostname ?? r.snapshot.deviceName ?? r.snapshot.deviceId ?? "—"}
                </td>
                <td className="px-3 py-2">
                  <OwnerPill
                    consoleUser={(r.snapshot as any).consoleUser ?? null}
                    deviceId={r.snapshot.deviceId}
                    size="sm"
                  />
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "inline-block rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider",
                      CHECK_COLOR[check] ?? CHECK_COLOR.cron,
                    )}
                  >
                    {check}
                  </span>
                </td>
                <td className="px-3 py-2 min-w-0 max-w-[280px]">
                  <div className="font-medium text-foreground truncate">{r.item.name ?? "—"}</div>
                  {r.item.path && (
                    <div className="text-[10px] font-mono text-muted-foreground truncate" title={r.item.path}>
                      {r.item.path}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-muted-foreground truncate">{r.item.user ?? "—"}</td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "inline-block rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider",
                      STATUS_COLOR[st],
                    )}
                  >
                    {st}
                  </span>
                </td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums" title={r.snapshot.capturedAt}>
                  {r.snapshot.capturedAt
                    ? formatDistanceToNow(new Date(r.snapshot.capturedAt), { addSuffix: true })
                    : "—"}
                </td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onApprove(r)}>
                        Approve into baseline
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onOpen(r)}>View detail</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ───────────────────────── Detail drawer ───────────────────────── */

function ItemDrawer({
  row,
  baselineEntries,
  onApprove,
}: {
  row: FlatRow;
  baselineEntries: PersistenceItem[] | null;
  onApprove: () => void;
}) {
  const { item, snapshot } = row;
  const sibling = (snapshot.items ?? []).filter((it) => it !== item).slice(0, 8);
  const status = (item.status ?? "match") as PersistenceItemStatus;
  const hasBaseline = baselineEntries !== null;
  const match = hasBaseline ? matchBaselineEntry(baselineEntries!, item) : { entry: null, on: null as MatchOn };

  // Build diff sides based on status. For "missing", baseline → empty. For "new", empty → snapshot.
  const diffLeft = status === "new" ? null : match.entry ?? null;
  const diffRight = status === "missing" ? null : item;

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          {item.name ?? item.path ?? "Item detail"}
          <span
            className={cn(
              "inline-block rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider",
              STATUS_COLOR[status],
            )}
          >
            {status}
          </span>
        </SheetTitle>
        <SheetDescription>
          {snapshot.checkType} on {snapshot.hostname ?? snapshot.deviceId}
        </SheetDescription>
      </SheetHeader>

      <div className="mt-4 space-y-4 text-xs">
        <KeyValue k="Path" v={item.path ?? "—"} mono />
        <KeyValue k="User" v={item.user ?? "—"} mono />
        <KeyValue k="Hash" v={item.hash ?? "—"} mono truncate />
        <KeyValue
          k="Added"
          v={item.addedAt ? formatDistanceToNow(new Date(item.addedAt), { addSuffix: true }) : "—"}
        />
        <KeyValue
          k="Captured"
          v={
            snapshot.capturedAt
              ? formatDistanceToNow(new Date(snapshot.capturedAt), { addSuffix: true })
              : "—"
          }
        />

        {/* Baseline vs snapshot comparison */}
        <div className="pt-2 border-t border-border">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Baseline vs snapshot
            </div>
            {hasBaseline ? (
              match.entry ? (
                <Badge variant="outline" className="text-[10px] font-normal">
                  matched on {match.on}
                </Badge>
              ) : status === "new" ? (
                <Badge variant="outline" className="text-[10px] font-normal border-warning/40 text-warning">
                  not in baseline
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] font-normal">
                  no baseline counterpart
                </Badge>
              )
            ) : (
              <Badge variant="outline" className="text-[10px] font-normal border-warning/40 text-warning">
                baseline not set
              </Badge>
            )}
          </div>
          {!hasBaseline ? (
            <Alert className="border-warning/40 bg-warning/5">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertDescription className="text-[11px] text-muted-foreground">
                No baseline exists for <code>{String(snapshot.checkType)}</code>. Establish one from
                "Manage baselines" to enable accurate drift comparison — until then every entry is
                reported as <b>new</b>.
              </AlertDescription>
            </Alert>
          ) : (
            <AttestationDiff
              a={diffLeft ?? { _: "(no baseline entry)" }}
              b={diffRight ?? { _: "(missing from snapshot)" }}
            />
          )}
        </div>


        {item.metadata && Object.keys(item.metadata).length > 0 && (
          <details>
            <summary className="text-[10px] uppercase tracking-wider text-muted-foreground cursor-pointer">
              Metadata
            </summary>
            <pre className="mt-2 rounded-md bg-muted/40 p-2 font-mono text-[10px] overflow-x-auto">
              {JSON.stringify(item.metadata, null, 2)}
            </pre>
          </details>
        )}

        {sibling.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
              Other items captured in this snapshot
            </div>
            <ul className="space-y-1 border border-border rounded-md p-2 max-h-48 overflow-y-auto">
              {sibling.map((s, i) => (
                <li key={i} className="font-mono text-[11px] truncate" title={s.path}>
                  <span className="text-muted-foreground mr-1">·</span>
                  {s.name ?? s.path}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="pt-2 flex gap-2 border-t border-border">
          <Button size="sm" onClick={onApprove}>
            Approve into baseline
          </Button>
        </div>
      </div>
    </>
  );
}

function KeyValue({
  k,
  v,
  mono,
  truncate,
}: {
  k: string;
  v: string;
  mono?: boolean;
  truncate?: boolean;
}) {
  return (
    <div className="grid grid-cols-[100px_1fr] gap-3">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</span>
      <span
        className={cn(mono && "font-mono", truncate && "truncate", "text-foreground")}
        title={v}
      >
        {v}
      </span>
    </div>
  );
}

/* ───────────────────────── Approve into baseline dialog ───────────────────────── */

function ApproveIntoBaselineDialog({
  target,
  onOpenChange,
}: {
  target: FlatRow | null;
  onOpenChange: (open: boolean) => void;
  existingBaselineEntries?: (ct: string) => unknown;
}) {
  const [scope, setScope] = useState<"device" | "all">("device");
  const [reason, setReason] = useState("");
  const baselinesQ = usePersistenceBaselines();
  const replaceM = useReplaceBaseline();

  const checkType = target?.snapshot.checkType;
  const existing = useMemo(
    () => baselinesQ.data?.find((b) => b.checkType === checkType),
    [baselinesQ.data, checkType],
  );

  if (!target) {
    return (
      <Dialog open={false} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );
  }

  const item = target.item;

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve "{item.name ?? item.path ?? "item"}" into baseline?</DialogTitle>
          <DialogDescription>
            This adds the entry to the per-tenant baseline for {String(checkType)}. After approval,
            this entry is "expected" on every device — drift detection for this fingerprint will
            return to "match".
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Apply to</Label>
            <RadioGroup
              value={scope}
              onValueChange={(v) => setScope(v as "device" | "all")}
              className="mt-2 space-y-1"
            >
              <label className="flex items-center gap-2 text-xs">
                <RadioGroupItem value="device" id="scope-device" />
                <span>This device only</span>
              </label>
              <label className="flex items-center gap-2 text-xs">
                <RadioGroupItem value="all" id="scope-all" />
                <span>All devices</span>
              </label>
            </RadioGroup>
          </div>
          <div>
            <Label className="text-xs">Reason (audit log)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Approved by SecOps — vendor-supplied LaunchAgent"
              className="text-xs min-h-[60px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={replaceM.isPending || !checkType}
            onClick={() => {
              if (!checkType) return;
              const nextEntries = [...(existing?.entries ?? []), item];
              replaceM.mutate(
                { checkType, entries: nextEntries },
                {
                  onSuccess: () => {
                    toast.success("Added to baseline");
                    onOpenChange(false);
                  },
                  onError: (e: any) => toast.error(e?.message ?? "Failed to update baseline"),
                },
              );
            }}
          >
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Approved writers dialog ───────────────────────── */

function ApprovedWritersDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const writersQ = useApprovedWriters();
  const addM = useAddApprovedWriter();
  const removeM = useRemoveApprovedWriter();
  const [kind, setKind] = useState<WriterKind>("team_id");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");

  const writers = writersQ.data?.tenant ?? [];
  const defaults = writersQ.data?.defaults ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Approved writers</DialogTitle>
          <DialogDescription>
            Entries written by these binaries are NOT flagged as drift even when they aren't in a
            baseline — useful for vendor installers and MDM agents.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Add row */}
          <div className="rounded-md border border-border bg-muted/20 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Add writer
            </div>
            <div className="grid grid-cols-[140px_1fr_1fr_auto] gap-2">
              <Select value={kind} onValueChange={(v) => setKind(v as WriterKind)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team_id">team_id</SelectItem>
                  <SelectItem value="signing_id">signing_id</SelectItem>
                  <SelectItem value="process_path">process_path</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={
                  kind === "team_id" ? "UBF8T346G9" : kind === "signing_id" ? "com.apple.*" : "/usr/local/bin/jamf"
                }
                className="h-8 text-xs font-mono"
              />
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (e.g. Apple)"
                className="h-8 text-xs"
              />
              <Button
                size="sm"
                className="h-8"
                disabled={!value.trim() || addM.isPending}
                onClick={() =>
                  addM.mutate(
                    { kind, value: value.trim(), note: note.trim() || undefined },
                    {
                      onSuccess: () => {
                        toast.success("Writer added");
                        setValue("");
                        setNote("");
                      },
                      onError: (e: any) => toast.error(e?.message ?? "Failed to add"),
                    },
                  )
                }
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* Tenant writers */}
          {writersQ.isLoading ? (
            <div className="text-xs text-muted-foreground">Loading…</div>
          ) : writers.length === 0 ? (
            <div className="py-4 text-center text-xs text-muted-foreground">
              No tenant writers yet.
            </div>
          ) : (
            <ul className="border border-border rounded-md divide-y divide-border">
              {writers.map((w) => (
                <li key={`${w.kind}-${w.value}`} className="px-3 py-2 flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="text-[10px] font-normal">{w.kind}</Badge>
                  <code className="font-mono truncate flex-1">{w.value}</code>
                  {w.note && <span className="text-muted-foreground truncate max-w-[140px]">{w.note}</span>}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      removeM.mutate(
                        { kind: w.kind, value: w.value },
                        {
                          onSuccess: () => toast.success("Writer removed"),
                          onError: (e: any) => toast.error(e?.message ?? "Failed"),
                        },
                      )
                    }
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {defaults.length > 0 && (
            <details>
              <summary className="text-[10px] uppercase tracking-wider text-muted-foreground cursor-pointer">
                Built-in defaults ({defaults.length})
              </summary>
              <ul className="mt-2 border border-border rounded-md divide-y divide-border">
                {defaults.map((w) => (
                  <li key={`def-${w.kind}-${w.value}`} className="px-3 py-2 flex items-center gap-2 text-xs">
                    <Badge variant="outline" className="text-[10px] font-normal">{w.kind}</Badge>
                    <code className="font-mono truncate flex-1">{w.value}</code>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Baselines dialog ───────────────────────── */

function BaselinesDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const baselinesQ = usePersistenceBaselines();
  const baselines = baselinesQ.data ?? [];
  const replaceM = useReplaceBaseline();
  const [editing, setEditing] = useState<PersistenceCheckType | null>(null);
  const [draft, setDraft] = useState<string>("");

  function openEditor(ct: PersistenceCheckType) {
    setEditing(ct);
    const b = baselines.find((x) => x.checkType === ct);
    setDraft(JSON.stringify(b?.entries ?? [], null, 2));
  }

  function save() {
    if (!editing) return;
    let parsed: PersistenceItem[] = [];
    try {
      parsed = JSON.parse(draft);
      if (!Array.isArray(parsed)) throw new Error("Not an array");
    } catch (e: any) {
      toast.error(`Invalid JSON: ${e?.message ?? "parse error"}`);
      return;
    }
    replaceM.mutate(
      { checkType: editing, entries: parsed },
      {
        onSuccess: () => {
          toast.success(`${editing} baseline updated`);
          setEditing(null);
        },
        onError: (e: any) => toast.error(e?.message ?? "Failed to save baseline"),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Manage baselines</DialogTitle>
          <DialogDescription>
            One baseline per check type. Drift detection compares each device's snapshot against
            these approved entries.
          </DialogDescription>
        </DialogHeader>

        {editing ? (
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Editing: {editing}
            </div>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="font-mono text-[11px] min-h-[360px]"
              spellCheck={false}
            />
            <div className="text-[10px] text-muted-foreground">
              Paste a JSON array of entries. Each entry: <code>{`{ name, path, hash, user, … }`}</code>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Back</Button>
              <Button onClick={save} disabled={replaceM.isPending}>Save baseline</Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CHECK_TYPES.map((ct) => {
              const b = baselines.find((x) => x.checkType === ct);
              return (
                <button
                  key={ct}
                  onClick={() => openEditor(ct)}
                  className="text-left rounded-md border border-border p-3 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "inline-block rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider",
                        CHECK_COLOR[ct] ?? CHECK_COLOR.cron,
                      )}
                    >
                      {ct}
                    </span>
                    <span className="text-sm font-medium text-foreground">{ct} baseline</span>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {b ? `${b.entryCount ?? b.entries?.length ?? 0} approved entries` : "Not set"}
                  </div>
                  {b?.updatedAt && (
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      Updated {formatDistanceToNow(new Date(b.updatedAt), { addSuffix: true })}
                      {b.updatedBy ? ` by ${b.updatedBy}` : ""}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
